import { combineAudioData, getAudioFrequencyData, getBarColor } from './audio-utils';
import { onLocalAudioTrack, onRemoteAudioTrack } from './livekit';
import { getLogger } from './logger';
import type { RemoteTrack } from './types';

const logger = getLogger('UI');

/**
 * Visualizer state management
 */
interface VisualizerState {
  instance: AudioVisualizer | null;
  pendingVisualizer: { svgElement: Element; text: string } | null;
  isCreating: boolean;
}

const state: VisualizerState = {
  instance: null,
  pendingVisualizer: null,
  isCreating: false,
};

/**
 * Audio visualizer interface for dynamic frequency waveform
 */
export interface AudioVisualizer {
  container: SVGGElement;
  bars: SVGPathElement[];
  animationId: number | null;
  targetHeights: number[];
  currentHeights: number[];
  isActive: boolean;
  lastUpdateTime: number;
  frameRate: number;
  frameCount: number;
  frameRateUpdateTime: number;
  remoteAnalyser?: AnalyserNode;
  localAnalyser?: AnalyserNode;
  cleanup?: () => Promise<void>;
}

/**
 * Visualizer configuration constants
 */
const VISUALIZER_CONFIG = {
  // Position matching state visualizations
  centerX: 580,
  centerY: 340,

  // Waveform configuration - optimized for human speech (85Hz-4kHz)
  pointsPerSide: 16, // Reduced from 32 - 32 bars total instead of 64
  barWidth: 5, // Increased from 3 for better visibility with fewer bars
  barGap: 4, // Increased from 2 for better spacing
  maxHeight: 80,
  minHeight: 4, // Minimum height when silent

  // Frequency range - use only lower ~40% of spectrum for speech (0-4kHz)
  speechFrequencyRatio: 0.4,

  // Animation configuration
  targetFrameRate: 30,
  smoothingFactor: 0.2,
  idleAnimationSpeed: 0.02,
  silenceThreshold: 0.01, // Lowered threshold - below this, consider as silence
  silenceDecay: 0.95, // Fast decay when silence detected

  // Colors
  colorBase: '#4CAF50',
  colorHigh: '#81C784',
  colorLow: '#388E3C',
  colorIdle: '#2E7D32', // Darker green for idle state

  // Performance
  throttleMs: 1000 / 30,

  // Debug
  debugInterval: 5000, // Log audio levels every 5 seconds
} as const;

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Initialize audio analyser from LiveKit track (works for both LocalAudioTrack and RemoteAudioTrack)
 */
async function initializeAudioAnalyser(
  track: RemoteTrack | any
): Promise<{ analyser: AnalyserNode; cleanup: () => Promise<void> }> {
  logger.debug('Initializing audio analyser', {
    hasTrack: !!track,
    trackKind: track?.kind,
    trackType: typeof track,
  });

  try {
    if (!track) {
      throw new Error('Track is null or undefined');
    }

    // Check what LiveKit client has available
    const livekitClient = (window as unknown as { LivekitClient: unknown }).LivekitClient;

    if (!livekitClient || typeof livekitClient !== 'object') {
      logger.error('LiveKit client not loaded or not an object', {
        hasLivekitClient: !!livekitClient,
        type: typeof livekitClient,
      });
      throw new Error('LiveKit client not loaded');
    }

    // Check if createAudioAnalyser exists
    const livekit = livekitClient as { createAudioAnalyser?: unknown; [key: string]: unknown };

    if (typeof livekit.createAudioAnalyser !== 'function') {
      logger.error('createAudioAnalyser not found in LiveKit client', {
        hasCreateAudioAnalyser: 'createAudioAnalyser' in livekit,
        createAudioAnalyserType: typeof livekit.createAudioAnalyser,
      });
      throw new Error('createAudioAnalyser not available in LiveKit client');
    }

    logger.debug('Calling createAudioAnalyser');
    const createAudioAnalyser = livekit.createAudioAnalyser as (
      track: RemoteTrack | any,
      options: unknown
    ) => unknown;

    const result = createAudioAnalyser(track, {
      fftSize: 256,
      smoothingTimeConstant: 0.8,
      minDecibels: -90,
      maxDecibels: -10,
    });

    logger.debug('createAudioAnalyser returned', {
      resultType: typeof result,
      resultKeys: result && typeof result === 'object' ? Object.keys(result) : 'not an object',
    });

    if (!result || typeof result !== 'object') {
      throw new Error('createAudioAnalyser did not return an object');
    }

    const resultObj = result as { analyser?: unknown; cleanup?: unknown };
    // Use duck typing instead of instanceof for better test compatibility
    // Check for required AnalyserNode properties
    const isValidAnalyser =
      resultObj.analyser &&
      typeof resultObj.analyser === 'object' &&
      'frequencyBinCount' in resultObj.analyser &&
      'fftSize' in resultObj.analyser &&
      'getByteFrequencyData' in resultObj.analyser;

    if (!isValidAnalyser) {
      logger.error('createAudioAnalyser result does not contain valid analyser', {
        hasAnalyser: !!resultObj.analyser,
        analyserType: typeof resultObj.analyser,
        analyserConstructor: resultObj.analyser?.constructor?.name,
      });
      throw new Error('createAudioAnalyser did not return a valid AnalyserNode');
    }

    const analyser = resultObj.analyser as AnalyserNode;
    logger.info('Audio analyser initialized successfully', {
      frequencyBinCount: analyser.frequencyBinCount,
      fftSize: analyser.fftSize,
    });

    return {
      analyser: resultObj.analyser as AnalyserNode,
      cleanup: resultObj.cleanup as () => Promise<void>,
    };
  } catch (error) {
    logger.error('Failed to initialize audio analyser', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
    });
    throw error;
  }
}

/**
 * Create waveform bars
 */
function createWaveformBars(container: SVGGElement): {
  bars: SVGPathElement[];
  targetHeights: number[];
  currentHeights: number[];
} {
  const bars: SVGPathElement[] = [];
  const targetHeights: number[] = [];
  const currentHeights: number[] = [];
  const totalPoints = VISUALIZER_CONFIG.pointsPerSide * 2;
  const { centerX, centerY, barWidth, barGap, minHeight } = VISUALIZER_CONFIG;

  for (let i = 0; i < totalPoints; i++) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('class', 'audio-bar');
    path.setAttribute('stroke-linecap', 'butt');
    path.setAttribute('stroke-width', VISUALIZER_CONFIG.barWidth.toString());

    // Performance optimization hints
    path.style.willChange = 'stroke, d';
    path.style.transform = 'translateZ(0)';

    // Set initial position to idle state to prevent flicker
    const sideIndex = i < VISUALIZER_CONFIG.pointsPerSide ? i : i - VISUALIZER_CONFIG.pointsPerSide;
    const isLeftSide = i < VISUALIZER_CONFIG.pointsPerSide;
    const xOffset = sideIndex * (barWidth + barGap) + (barWidth + barGap) / 2;
    const x = isLeftSide ? centerX - xOffset : centerX + xOffset;
    const y1 = centerY - minHeight;
    const y2 = centerY + minHeight;
    path.setAttribute('d', `M ${x} ${y1} L ${x} ${y2}`);
    path.setAttribute(
      'stroke',
      getBarColor(
        0,
        VISUALIZER_CONFIG.colorLow,
        VISUALIZER_CONFIG.colorBase,
        VISUALIZER_CONFIG.colorHigh
      )
    );

    container.appendChild(path);

    bars.push(path);
    targetHeights.push(VISUALIZER_CONFIG.minHeight);
    currentHeights.push(VISUALIZER_CONFIG.minHeight);
  }

  return { bars, targetHeights, currentHeights };
}

/**
 * Update waveform bars
 */
function updateWaveformBars(visualizer: AudioVisualizer, audioLevels: number[]): void {
  const { bars, targetHeights, currentHeights } = visualizer;
  const {
    centerX,
    centerY,
    pointsPerSide,
    maxHeight,
    minHeight,
    barWidth,
    barGap,
    silenceThreshold,
    silenceDecay,
  } = VISUALIZER_CONFIG;

  // Calculate average audio level to detect silence
  const avgLevel = audioLevels.reduce((sum, level) => sum + level, 0) / audioLevels.length;
  const isSilent = avgLevel < silenceThreshold;

  for (let i = 0; i < bars.length; i++) {
    // Determine which side and position
    const sideIndex = i < pointsPerSide ? i : i - pointsPerSide;
    const isLeftSide = i < pointsPerSide;

    // Calculate target height with ease-in curve
    const intensity = audioLevels[sideIndex] || 0;
    let curvedIntensity = Math.pow(intensity, 0.7); // Ease in

    // If silent, decay quickly to idle state
    if (isSilent) {
      curvedIntensity = 0;
    }

    const targetHeight = Math.max(minHeight, curvedIntensity * maxHeight);

    // Smooth transition - use faster decay for silence
    const effectiveSmoothing =
      isSilent && currentHeights[i] > minHeight * 2
        ? silenceDecay
        : VISUALIZER_CONFIG.smoothingFactor;

    targetHeights[i] = targetHeight;
    currentHeights[i] += (targetHeights[i] - currentHeights[i]) * effectiveSmoothing;

    const height = currentHeights[i];

    // Calculate bar position (mirrored from center with small gap)
    // Offset creates 2-pixel gap between left and right sides at center
    const xOffset = sideIndex * (barWidth + barGap) + (barWidth + barGap) / 2;
    const x = isLeftSide ? centerX - xOffset : centerX + xOffset;
    const y1 = centerY - height;
    const y2 = centerY + height;

    // Update path data (vertical line)
    const pathData = `M ${x} ${y1} L ${x} ${y2}`;
    bars[i].setAttribute('d', pathData);

    // Update color based on intensity - use idle color when silent
    const effectiveIntensity = isSilent ? 0 : intensity;
    bars[i].setAttribute(
      'stroke',
      getBarColor(
        effectiveIntensity,
        VISUALIZER_CONFIG.colorLow,
        VISUALIZER_CONFIG.colorBase,
        VISUALIZER_CONFIG.colorHigh
      )
    );
  }
}

/**
 * Update static visualizer for reduced motion preference
 */
function updateStaticVisualizer(visualizer: AudioVisualizer, audioLevels: number[]): void {
  // Show static visualization with current audio levels
  updateWaveformBars(visualizer, audioLevels);
}

/**
 * Register callback for missing track type
 * Returns true if callback was registered (meaning we need to wait), false if both tracks already present
 */
function ensureBothTracks(
  svgElement: Element,
  text: string,
  remoteTrack?: RemoteTrack,
  localTrack?: any
): boolean {
  // If we have both tracks, nothing to do
  if (remoteTrack && localTrack) {
    state.pendingVisualizer = null;
    return false;
  }

  // Define callback for when a track becomes available
  const handleTrackAvailable = async () => {
    if (state.pendingVisualizer && !state.isCreating) {
      const { svgElement: element, text: t } = state.pendingVisualizer;
      const { getRemoteAudioTrack, getLocalAudioTrack } = await import('./livekit');
      const remote = getRemoteAudioTrack();
      const local = getLocalAudioTrack();
      await createAudioVisualizer(element, t, remote ?? undefined, local);
    }
  };

  // Register callback for the missing track type
  if (!remoteTrack) {
    onRemoteAudioTrack(handleTrackAvailable);
  }
  if (!localTrack) {
    onLocalAudioTrack(handleTrackAvailable);
  }

  // Set pending if both are missing
  if (!remoteTrack && !localTrack) {
    state.pendingVisualizer = { svgElement, text };
  }

  return true;
}

/**
 * Create audio visualizer SVG elements
 */
export async function createAudioVisualizer(
  svgElement: Element,
  text: string,
  remoteTrack?: RemoteTrack,
  localTrack?: any
): Promise<void> {
  // Guard to prevent duplicate visualizer creation
  if (state.isCreating) {
    logger.debug('Visualizer creation already in progress, skipping');
    return;
  }
  state.isCreating = true;

  logger.debug('Creating audio visualizer', {
    hasRemoteTrack: !!remoteTrack,
    hasLocalTrack: !!localTrack,
    remoteTrackKind: remoteTrack?.kind,
    localTrackType: localTrack?.constructor?.name,
  });

  // Register callbacks for tracks that aren't available yet
  // Note: We don't wait for both tracks - we create visualizer with whatever is available
  ensureBothTracks(svgElement, text, remoteTrack, localTrack);

  // If we have NO tracks at all, wait and exit early
  if (!remoteTrack && !localTrack) {
    logger.debug('No tracks available yet, waiting for at least one track');
    state.isCreating = false;
    return;
  }

  try {
    // Check if visualizer already exists
    if (state.instance) {
      logger.warn('Audio visualizer already exists, cleaning up first');
      await stopAudioVisualizer();
    }
    // Create container group
    const container = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    container.setAttribute('id', 'audio-visualizer');
    container.setAttribute('class', 'audio-visualizer');
    container.setAttribute('role', 'img');

    // Create waveform bars
    const { bars, targetHeights, currentHeights } = createWaveformBars(container);

    // Add status text
    const statusText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    statusText.setAttribute('x', VISUALIZER_CONFIG.centerX.toString());
    statusText.setAttribute('y', (VISUALIZER_CONFIG.centerY + 125).toString());
    statusText.setAttribute('text-anchor', 'middle');
    statusText.setAttribute(
      'font-family',
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif'
    );
    statusText.setAttribute('font-size', '36');
    statusText.setAttribute('font-weight', '400');
    statusText.setAttribute('fill', '#333333');
    statusText.setAttribute('opacity', '1');
    statusText.textContent = text;

    container.appendChild(statusText);

    // Add container to SVG
    svgElement.appendChild(container);

    // Initialize audio analysers
    let remoteAnalyser: AnalyserNode | undefined;
    let localAnalyser: AnalyserNode | undefined;
    const cleanupFunctions: (() => Promise<void>)[] = [];

    if (remoteTrack) {
      try {
        const result = await initializeAudioAnalyser(remoteTrack);
        remoteAnalyser = result.analyser;
        cleanupFunctions.push(result.cleanup);
        logger.debug('Remote audio analyser initialized');
      } catch (error) {
        logger.warn('Failed to initialize remote audio analyser', { error });
      }
    }

    if (localTrack) {
      try {
        const result = await initializeAudioAnalyser(localTrack);
        localAnalyser = result.analyser;
        cleanupFunctions.push(result.cleanup);
        logger.debug('Local audio analyser initialized');
      } catch (error) {
        logger.warn('Failed to initialize local audio analyser', { error });
      }
    }

    // Create cleanup function that runs all cleanups
    const cleanup = async () => {
      await Promise.all(
        cleanupFunctions.map((fn) => fn().catch((e) => logger.warn('Cleanup error', { error: e })))
      );
    };

    // Create visualizer instance
    state.instance = {
      container,
      bars,
      animationId: null,
      targetHeights,
      currentHeights,
      isActive: true,
      lastUpdateTime: performance.now(),
      frameRate: 0,
      frameCount: 0,
      frameRateUpdateTime: performance.now(),
      remoteAnalyser,
      localAnalyser,
      cleanup,
    };

    // Start animation if user doesn't prefer reduced motion
    if (!prefersReducedMotion()) {
      startAudioVisualizerAnimation();
    } else {
      logger.debug('Reduced motion preferred, showing static visualizer');
      const audioLevels = getCombinedAudioData(remoteAnalyser, localAnalyser);
      updateStaticVisualizer(state.instance, audioLevels);
    }

    logger.info('Audio visualizer created successfully', {
      pointsPerSide: VISUALIZER_CONFIG.pointsPerSide,
      centerX: VISUALIZER_CONFIG.centerX,
      centerY: VISUALIZER_CONFIG.centerY,
      hasRemoteAnalyser: !!remoteAnalyser,
      hasLocalAnalyser: !!localAnalyser,
    });
  } catch (error) {
    logger.error('Failed to create audio visualizer', {
      error: error instanceof Error ? error.message : error,
    });
  } finally {
    state.isCreating = false;
  }
}

/**
 * Get combined audio data from both local and remote analysers
 */
function getCombinedAudioData(
  remoteAnalyser?: AnalyserNode,
  localAnalyser?: AnalyserNode
): number[] {
  const dataArrays: number[][] = [];

  if (remoteAnalyser) {
    dataArrays.push(getAudioFrequencyData(remoteAnalyser, VISUALIZER_CONFIG.pointsPerSide));
  }

  if (localAnalyser) {
    dataArrays.push(getAudioFrequencyData(localAnalyser, VISUALIZER_CONFIG.pointsPerSide));
  }

  return combineAudioData(dataArrays, VISUALIZER_CONFIG.pointsPerSide);
}

/**
 * Start audio visualizer animation loop
 */
function startAudioVisualizerAnimation(): void {
  if (!state.instance || !state.instance.isActive) {
    return;
  }

  const animate = (currentTime: number) => {
    if (!state.instance || !state.instance.isActive) {
      return;
    }

    const visualizer = state.instance;

    // Throttle animation to maintain target frame rate
    const deltaTime = currentTime - visualizer.lastUpdateTime;
    if (deltaTime < VISUALIZER_CONFIG.throttleMs) {
      visualizer.animationId = requestAnimationFrame(animate);
      return;
    }

    // Performance monitoring
    visualizer.frameCount++;
    const elapsed = currentTime - visualizer.frameRateUpdateTime;
    if (elapsed >= 2000) {
      visualizer.frameRate = visualizer.frameCount / (elapsed / 1000);
      visualizer.frameCount = 0;
      visualizer.frameRateUpdateTime = currentTime;

      // Log performance warnings if needed
      if (visualizer.frameRate < VISUALIZER_CONFIG.targetFrameRate * 0.8) {
        logger.warn('Audio visualizer performance below target', {
          currentFrameRate: Math.round(visualizer.frameRate),
          targetFrameRate: VISUALIZER_CONFIG.targetFrameRate,
        });
      }
    }

    // Get real audio data if available, otherwise use silent levels (idle state)
    const audioLevels = getCombinedAudioData(visualizer.remoteAnalyser, visualizer.localAnalyser);

    // Update waveform
    updateWaveformBars(visualizer, audioLevels);

    visualizer.lastUpdateTime = currentTime;
    visualizer.animationId = requestAnimationFrame(animate);
  };

  state.instance.animationId = requestAnimationFrame(animate);
  logger.debug('Audio visualizer animation started');
}

/**
 * Stop and clean up audio visualizer
 */
export async function stopAudioVisualizer(): Promise<void> {
  if (!state.instance) {
    return;
  }

  logger.debug('Stopping audio visualizer');

  state.instance.isActive = false;

  // Clear pending state
  state.pendingVisualizer = null;

  if (state.instance.animationId) {
    cancelAnimationFrame(state.instance.animationId);
    state.instance.animationId = null;
  }

  // Clean up audio analyser
  if (state.instance.cleanup) {
    try {
      await state.instance.cleanup();
    } catch (error) {
      logger.warn('Error cleaning up audio analyser', { error });
    }
  }

  if (state.instance.container.parentNode) {
    state.instance.container.parentNode.removeChild(state.instance.container);
  }

  state.instance = null;

  logger.debug('Audio visualizer stopped and cleaned up');
}
